import { useParams } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useMemo } from 'react';

import type { PhotosDeps, PhotoUploadFn } from '@/features/photos';
import { createPhotosApi, MyPhotoPage, MyPhotosPage, PhotosProvider } from '@/features/photos';
import { webShare } from '@/lib/share';

import { clientFetcher } from './bp03';
import { useClientDeps } from './deps';

/**
 * BP-13 §6's two client rows: `/me/photos` and `/me/photos/:photoId`.
 *
 * **A client never uploads.** Rule 1 puts the shutter in the technician's
 * hands — a photograph is taken during a service, by somebody the salon
 * employs, against a consent recorded at the desk. The uploader this app
 * passes in therefore refuses rather than sending anything: `PhotosApi` has
 * one shape for both apps, and the honest way to say "not from here" is a
 * function that cannot succeed, not a route the api would 403.
 *
 * Deleting is the same: `canDelete` is `false`, because `session.manage` is a
 * staff permission and the client's own route out is BP-03's erasure request,
 * which takes the whole record rather than one picture.
 */

/** The uploader the client app supplies: one that refuses (see above). */
export const clientUpload: PhotoUploadFn = () =>
  Promise.reject(new Error('BP-13 rule 1: only staff capture photographs'));

/** The photo feature's dependencies for the signed-in client. */
export function useClientPhotosDeps(): PhotosDeps {
  const deps = useClientDeps();
  const fetcher = clientFetcher(deps.fetcher);
  const api = useMemo(() => createPhotosApi(fetcher, clientUpload), [fetcher]);
  return { api, canCapture: false, canDelete: false, share: webShare() };
}

/** Wrap a §6 client screen in the feature provider. */
function PhotosRoute({ children }: { children: JSX.Element }): JSX.Element {
  return <PhotosProvider deps={useClientPhotosDeps()}>{children}</PhotosProvider>;
}

/** §6 client `/me/photos`. */
export function MyPhotosRoute(): JSX.Element {
  return (
    <PhotosRoute>
      <MyPhotosPage />
    </PhotosRoute>
  );
}

/**
 * The photograph id off the path, or `''`.
 *
 * A named function rather than an inline `??`, the shape `bp12.tsx`'s
 * `skuIdOf` set: `MyPhotoPage` answers "not found" for `''` without a router
 * in the test.
 */
export function photoIdOf(params: { photoId?: string }): string {
  return params.photoId ?? '';
}

/** §6 client `/me/photos/:photoId`. */
export function MyPhotoRoute(): JSX.Element {
  const params: { photoId?: string } = useParams({ strict: false });
  return (
    <PhotosRoute>
      <MyPhotoPage photoId={photoIdOf(params)} />
    </PhotosRoute>
  );
}
